import React from "react";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";


const BaseMainFilterAutocomplete = ({
  label,
  value,
  onChange,
  options = [],
  accessor = "name",
  multiple = false,
  error = false,
  helperText = "",
  disabled = false,
  loading = false,
  enableAllOption = false,
  enableNorthAmerica = false,
  enableNonNorthAmerica = false,
  gridProps = { xs: 12, sm: 6, md: 3 },
  size = "small",
  northAmericaCountries = ["UNITED STATES", "CANADA", "MEXICO"],
  ...otherProps
}) => {
  const ALL_OPTION = { id: "ALL", [accessor]: "ALL", isAllOption: true };
  const NORTH_AMERICA_OPTION = { id: "NORTH AMERICA", [accessor]: "NORTH AMERICA", isNorthAmericaOption: true };
  const NON_NORTH_AMERICA_OPTION = { id: "NON NORTH AMERICA", [accessor]: "NON NORTH AMERICA", isNonNorthAmericaOption: true };
  const northAmericaSet = React.useMemo(() => new Set(northAmericaCountries), [northAmericaCountries]);
  const getOptionName = React.useCallback(
    (item) => {
      if (!item) return "";
      if (typeof item === "string") {
        return item;
      }
      return item[accessor] ?? "";
    },
    [accessor]
  );

  const finalOptions = React.useMemo(() => {
    const baseOptions = [...options];
    const specialOptions = [];
    
    if (enableAllOption) {
      specialOptions.push(ALL_OPTION);
    }
    if (enableNorthAmerica) {
      specialOptions.push(NORTH_AMERICA_OPTION);
    }
    if (enableNonNorthAmerica) {
      const hasNonNorthAmericaOptions = options.some((opt) => !northAmericaSet.has(getOptionName(opt)));
      if (hasNonNorthAmericaOptions) {
        specialOptions.push(NON_NORTH_AMERICA_OPTION);
      }
    }
    
    return [...specialOptions, ...baseOptions];
  }, [options, enableAllOption, enableNorthAmerica, enableNonNorthAmerica, getOptionName, northAmericaSet]);

  const normalizeValue = (val) => {
    if (multiple) {
      if (!val || (Array.isArray(val) && val.length === 0)) {
        return [];
      }
      if (Array.isArray(val) && val.length >= options.length) {
        return enableAllOption ? [ALL_OPTION] : val;
      }
      
      if (enableNorthAmerica) {
        const names = Array.isArray(val) ? val.map((v) => getOptionName(v)).filter(Boolean) : [];
        const isNorthAmerica = names.length === northAmericaCountries.length && 
                               names.every(n => northAmericaSet.has(n));
        
        if (isNorthAmerica) {
          return [NORTH_AMERICA_OPTION];
        }
      }

      if (enableNonNorthAmerica) {
        const names = Array.isArray(val) ? val.map((v) => getOptionName(v)).filter(Boolean) : [];
        const nonNorthAmericaOptions = options.filter((c) => !northAmericaSet.has(getOptionName(c)));
        const nonNorthAmericaSet = new Set(nonNorthAmericaOptions.map((c) => getOptionName(c))); 
        const isNonNorthAmerica = names.length === nonNorthAmericaSet.size &&
                                  names.every((n) => nonNorthAmericaSet.has(n));

        if (isNonNorthAmerica && nonNorthAmericaSet.size > 0) {
          return [NON_NORTH_AMERICA_OPTION];
        }
      }
      
      return Array.isArray(val) ? val : [val];
    } else {
      if (!val || (Array.isArray(val) && val.length === 0)) {
        return null;
      }
      if (Array.isArray(val) && val.length >= options.length) {
        return enableAllOption ? ALL_OPTION : val[0];
      }
      
      if (enableNorthAmerica) {
        const names = Array.isArray(val) ? val.map((v) => getOptionName(v)).filter(Boolean) : [getOptionName(val)];
        const isNorthAmerica = names.length === northAmericaCountries.length && 
                               names.every(n => northAmericaSet.has(n));
        
        if (isNorthAmerica) {
          return NORTH_AMERICA_OPTION;
        }
      }

      if (enableNonNorthAmerica) {
        const names = Array.isArray(val) ? val.map((v) => getOptionName(v)).filter(Boolean) : [getOptionName(val)];
        const nonNorthAmericaOptions = options.filter((c) => !northAmericaSet.has(getOptionName(c)));
        const nonNorthAmericaSet = new Set(nonNorthAmericaOptions.map((c) => getOptionName(c)));
        const isNonNorthAmerica = names.length === nonNorthAmericaSet.size &&
                                  names.every((n) => nonNorthAmericaSet.has(n));

        if (isNonNorthAmerica && nonNorthAmericaSet.size > 0) {
          return NON_NORTH_AMERICA_OPTION;
        }
      }
      
      return Array.isArray(val) ? val[0] : val;
    }
  };

  const handleSelectionChange = (newValue) => {
    if (multiple) {
      const hasAll = newValue?.some((n) => getOptionName(n) === "ALL" || n?.isAllOption);
      const hasNorthAmerica = newValue?.some((n) => getOptionName(n) === "NORTH AMERICA" || n?.isNorthAmericaOption);
      const hasNonNorthAmerica = newValue?.some((n) => getOptionName(n) === "NON NORTH AMERICA" || n?.isNonNorthAmericaOption);

      if (hasAll && enableAllOption) {
        const allOptions = options.filter((opt) => !(typeof opt === "object" && (opt.isAllOption || opt.isNorthAmericaOption || opt.isNonNorthAmericaOption)));
        onChange(allOptions);
      } else if (hasNorthAmerica && enableNorthAmerica) {
        const northAmericaOptions = options.filter((c) => northAmericaSet.has(getOptionName(c)));
        onChange(northAmericaOptions);
      } else if (hasNonNorthAmerica && enableNonNorthAmerica) {
        const nonNorthAmericaOptions = options.filter((c) => !northAmericaSet.has(getOptionName(c)));
        onChange(nonNorthAmericaOptions);
      } else {
        onChange(newValue || []);
      }
    } else {
      if (!newValue) {
        onChange(null);
        return;
      }

      const optionName = getOptionName(newValue);

      if (optionName === "ALL" || newValue?.isAllOption) {
        if (enableAllOption) {
          const allOptions = options.filter((opt) => !(typeof opt === "object" && (opt.isAllOption || opt.isNorthAmericaOption || opt.isNonNorthAmericaOption)));
          onChange(allOptions);
        }
      } else if (optionName === "NORTH AMERICA" || newValue?.isNorthAmericaOption) {
        if (enableNorthAmerica) {
          const northAmericaOptions = options.filter((c) => northAmericaSet.has(getOptionName(c)));
          onChange(northAmericaOptions);
        }
      } else if (optionName === "NON NORTH AMERICA" || newValue?.isNonNorthAmericaOption) {
        if (enableNonNorthAmerica) {
          const nonNorthAmericaOptions = options.filter((c) => !northAmericaSet.has(getOptionName(c)));
          onChange(nonNorthAmericaOptions);
        }
      } else {
        onChange(newValue);
      }
    }
  };

  const normalizedValue = normalizeValue(value);

  return (
    <Grid item {...gridProps}>
      <Autocomplete
        value={normalizedValue}
        multiple={multiple}
        options={finalOptions}
        disabled={disabled}
        size={size}
        fullWidth={true}
        getOptionLabel={(option) => {
          if (!option) return "";
          if (typeof option === "string") return option;
          return option[accessor] || "";
        }}
        isOptionEqualToValue={(option, val) => {
          if (!option && !val) return true;
          if (!option || !val) return false;
          if (typeof option === "string" && typeof val === "string") {
            return option === val;
          }
          if (accessor && option[accessor] && val[accessor]) {
            return option[accessor] === val[accessor];
          }
          return option === val;
        }}
        onChange={(_, newValue) => handleSelectionChange(newValue)}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            variant="outlined"
            error={error}
            helperText={helperText}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading && (
                    <CircularProgress
                      data-testid="loading-spinner"
                      color="inherit"
                      size={20}
                    />
                  )}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        {...otherProps}
      />
    </Grid>
  );
};

export default BaseMainFilterAutocomplete;